import {observable, action, makeObservable} from "mobx";
import ScrollManager from "window-scroll-manager";
import {WindowManager} from "react-window-decorators";

import {mobileNavWidth, primaryNavHeight} from "../utils/Settings";
import {domReady} from "../utils/DomUtils";
import {isSchoolBlocksApp} from "../utils/SchoolBlocksUtilities";
import {getConfig} from "../_configs/AppConfig";

const {publicRuntimeConfig} = getConfig();
/**
 * Copyright (c) 2013-present, Facebook, Inc.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @providesModule ExecutionEnvironment
 */

const canUseDOM = !!(
    typeof window !== "undefined" &&
    window.document &&
    window.document.createElement
);

function areThirdPartyCookiesEnabled(callback) {
    if (canUseDOM) {
        const body = document.getElementsByTagName('body')[0];

        // @ts-ignore
        window.cookie_has_been_set_by_third_party = function() {
            const check_cookies = document.createElement('script');
            check_cookies.setAttribute('src', `${publicRuntimeConfig.COOKIE_TEST_URL}?intent=check`);
            check_cookies.setAttribute('async', 'async');
            body.appendChild(check_cookies);
        }

        // @ts-ignore
        window.cookies_test_finished = function(enabled) {
            callback(enabled);
        }

        domReady(() => {
            const set_cookies = document.createElement('script');
            set_cookies.setAttribute('src', `${publicRuntimeConfig.COOKIE_TEST_URL}?intent=set`);
            set_cookies.setAttribute('async', 'async');
            if (body && body.appendChild) {
                body.appendChild(set_cookies);
            }
        })
    }
}



/**
 * Simple, lightweight module assisting with the detection and context of
 * Worker. Helps avoid circular dependencies and allows code to reason about
 * whether or not they are in a Worker, even if they never include the main
 * `ReactWorker` dependency.
 */
export const ExecutionEnvironment = {

    canUseDOM: canUseDOM,

    canUseWorkers: typeof Worker !== "undefined",

    canUseEventListeners: canUseDOM && !!(window.addEventListener),

    canUseViewport: canUseDOM && !!window.screen,

    isInWorker: !canUseDOM, // For now, this is true - might change in the future.

};

export const enum IView {
    MAP= "map",
    ALPHA = "alpha",
    DEFAULT = "default",
    CLASSROOM = "classroom",
    PAGE = "page",
    FOLLOWING = "following",
    SEARCH = "search",
    ERROR = "error",
    LOGIN = "login",
    USER_SETTINGS = "userSettings",
    ORGANIZATION_SETTINGS = "organizationSettings",
    OFFLINE = "offline",
    CALENDAR = "calendar",
    ORGANIZATION_PAGE = "organization",
}

export default class InterfaceStore {
    scrollPosition = 0;
    dimensions = {
        width: 0,
        height: 0,
    };
    width = 0;
    height = 0;
    breakpoint = 'break-point-xs';
    orientation = null;
    userAgent: string = "";
    isMobileAccountMenuOpen = false;
    isMobileNavMenuOpen = false;
    isSchoolBlocks = false;
    isTouchDevice = false;
    thirdPartyCookiesEnabled = true;
    currentFullUrl: string | null = null;
    userMenuOpen = false;
    isEditingPage = false;
    schoolFeedWidgetOpen = false;
    view: IView = IView.DEFAULT;

    public ExecutionEnvironment;
    public scrollManager;
    public windowManager;

    constructor() {
        // window manager should be used for resize
        // scroll manager should be used for scroll
        makeObservable(this, {
            scrollPosition: observable,
            breakpoint: observable,
            width: observable,
            height: observable,
            userMenuOpen: observable,
            toggleUserMenu: action,
            orientation: observable,
            isTouchDevice: observable,
            currentFullUrl: observable,
            userAgent: observable,
            setCurrentFullUrl: action,
            handleBrowserScroll: action,
            setBreakpoint: action,
            handleWindowResize: action,
            thirdPartyCookiesEnabled: observable,
            isEditingPage: observable,
            isSchoolBlocks: observable,
            toggleEditingPage: action,
            startEditingPage: action,
            stopEditingPage: action,
            isMobileAccountMenuOpen: observable,
            isMobileNavMenuOpen: observable,
            toggleMobileAccountMenu: action,
            toggleMobileNavMenu: action,
            closeAllMenus: action,
            setUserAgent: action,
            schoolFeedWidgetOpen: observable,
            toggleSchoolFeedWidgetOpen: action,
            view: observable,
            setView: action,
        });

        areThirdPartyCookiesEnabled(enabled => {
            this.thirdPartyCookiesEnabled = enabled;
        });

        this.ExecutionEnvironment = ExecutionEnvironment;
        this.isSchoolBlocks = isSchoolBlocksApp();

        this.scrollManager = new ScrollManager();
        this.windowManager = new WindowManager();
        if (this.ExecutionEnvironment.canUseDOM) {
            this.isTouchDevice = !!('ontouchstart' in window || navigator?.maxTouchPoints);

            window.addEventListener(
                "window-scroll",
                this.handleBrowserScroll,
                {passive: true}
            );
            this.scrollManager.handleScroll();

            window.addEventListener &&
            window.addEventListener(
                "window-resize",
                this.handleWindowResize,
                {passive: true}
            );
            // set the dimensions initially.
            this.dimensions = this.windowManager.getDimensions();
            this.setBreakpoint();

            // establish the window resize event listener
            this.windowManager.handleResize();

            if (window.location) {
                this.setCurrentFullUrl(window.location.href);
            }
        }
    }

    setUserAgent(userAgent) {
        // to be run on the server so we can take a good guess at the number of grid columns we should display on initial render
        // https://github.com/google/closure-library/blob/master/closure/goog/labs/useragent/device.js
        this.userAgent = userAgent;

        if (
            /iPod/.exec(userAgent) ||
            /iPhone/.exec(userAgent) ||
            /Android/.exec(userAgent) ||
            /IEMobile/.exec(userAgent)
        ) {
            this.breakpoint = "break-point-xs";
        } else if (
            /iPad/.exec(userAgent) ||
            /Silk/.exec(userAgent)
        ) {
            this.breakpoint = "break-point-sm";
        } else {
            this.breakpoint = "break-point-lg";
        }
    }

    setView(view: IView) {
        this.view = view;
    }

    toggleSchoolFeedWidgetOpen() {
        this.schoolFeedWidgetOpen = !this.schoolFeedWidgetOpen;
    }

    setCurrentFullUrl(url) {
        this.currentFullUrl = url;
    }

    closeAllMenus = () => {
        this.isMobileAccountMenuOpen = false;
        this.isMobileNavMenuOpen = false;
    }

    toggleUserMenu = () => {
        this.userMenuOpen = !this.userMenuOpen;
    }

    toggleEditingPage = () => {
        this.isEditingPage = !this.isEditingPage;
    }

    toggleMobileAccountMenu = () => {
        this.isMobileAccountMenuOpen = !this.isMobileAccountMenuOpen;
    }

    toggleMobileNavMenu = () => {
        this.isMobileNavMenuOpen = !this.isMobileNavMenuOpen;
    }

    startEditingPage = () => {
        this.isEditingPage = true;
    }

    stopEditingPage = () => {
        this.isEditingPage = false;
    }

    handleBrowserScroll = (e) => {
        this.scrollPosition = e.detail.scrollPosition;
    }

    setBreakpoint() {
        let bodyStyle: undefined | CSSStyleDeclaration = undefined;
        try {
            bodyStyle = window.getComputedStyle(document.body, ':before');
        } catch (e) {
            // pass.  If we can't get the computed style, then we need to move on and let the resize handler pick it up
            console.error(e);
        }

        if (bodyStyle !== undefined) {
            this.breakpoint = bodyStyle.getPropertyValue('content')
                .replace(/\"/g, '');
        }
    }

    handleWindowResize = (e) => {
        const doUpdate = action(() => {
            this.orientation = e.detail.orientation;
            this.dimensions = e.detail.dimensions;
            this.width = e.detail.dimensions.width;
            this.height = e.detail.dimensions.height;

            // Maybe we use the library's breakpoint functionality in the future.  For now, we have our own
            // that comes in through our SCSS stuff so we define it *one time* and can use it in CSS and JS.
            //this.breakpoint = e.detail.breakpoint;
            this.setBreakpoint();
        });

        if (requestAnimationFrame) {
            requestAnimationFrame(doUpdate);
        } else {
            doUpdate();
        }
    }

    zoomValue(): number {
        const zoom = Math.round(((( window.outerWidth - 10 ) / window.innerWidth) * 100)/5) * 5;
        return zoom;
    }

    scrollToId = id => {
        if (this.ExecutionEnvironment.canUseDOM && typeof document !== "undefined") {
            const blockToScroll = document.getElementById(id);

            if (blockToScroll) {
                const rect = blockToScroll.getBoundingClientRect();
                window.scroll({
                    top: this.scrollPosition + rect.top - (primaryNavHeight * (this.dimensions.width < mobileNavWidth ? 1 : 2)),
                    behavior: "smooth",  // Optional, adds animation
                })
            }
        }
    }
}
